import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useMotion } from '@/hooks/shared/useMotion';

/** The clip and the pre-measurement state of anything that grows in over a measured height: the fold itself, and the Overdue rows. */
export const foldLayoutStyles = StyleSheet.create({
  clip: { overflow: 'hidden' },
  // Nothing on screen while an arriving element waits for its measurement.
  unmeasured: { height: 0, opacity: 0 },
  // The measured view while its clip is pinned to a height. Yoga sizes an in-flow child to fit its parent, so on
  // native a view inside the collapsed (0-tall) clip would measure 0 and grow to nothing; out of flow it keeps its
  // natural height on every platform, and the clip's own height decides what shows.
  outOfFlow: { position: 'absolute', top: 0, left: 0, right: 0 },
});

type FoldPhase = 'closed' | 'opening' | 'open' | 'closing';

interface FoldProps {
  /** Whether the content should be up. Flipping it starts the transition. */
  open: boolean;
  /** False under reduced motion: mount or unmount at once. */
  animate: boolean;
  /** Style for the measured inner view (the Overdue block keeps its gaps there, so they fold away with it). */
  innerStyle?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

/**
 * Mounts `children` and grows them in when `open`; shrinks them away and
 * unmounts them when not. Same shape as a row's exit: the outer view is what
 * animates (height is a layout prop, so this runs on the JS driver — it is
 * what makes everything below move with it); the inner view keeps the natural
 * height and is where that height gets measured — out of flow while the outer
 * view is pinned, so the clip can't squash it (see outOfFlow). Opening content is clipped
 * to nothing until its first measurement lands, then grows over it, re-aimed
 * if the content grows meanwhile. A flip mid-way turns around from wherever
 * the value is instead of jumping.
 */
export const Fold: React.FC<FoldProps> = ({ open, animate, innerStyle, children }) => {
  const motion = useMotion();
  // Synced during render, not in an effect, so the frame that flips `open` is
  // the frame the fold reacts — no in-between commit.
  const [phase, setPhase] = useState<FoldPhase>(open ? 'open' : 'closed');
  if (open && (phase === 'closed' || phase === 'closing')) {
    setPhase(animate ? 'opening' : 'open');
  } else if (!open && (phase === 'open' || phase === 'opening')) {
    setPhase(animate ? 'closing' : 'closed');
  }

  const progress = useRef(new Animated.Value(open ? 1 : 0)).current;
  const heightRef = useRef<number | null>(null);
  // The height the outer view is animating over; null = natural layout (or
  // not yet measured while opening).
  const [height, setHeight] = useState<number | null>(null);
  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const measured = e.nativeEvent.layout.height;
      heightRef.current = measured;
      // Opening content follows its own growth: a row can still land mid-open.
      if (phase === 'opening') setHeight(measured);
    },
    [phase],
  );

  // Closing shrinks over the height measured as it starts. Open and closed
  // park the value at their end, so the next transition starts from it.
  useEffect(() => {
    if (phase === 'closed') {
      progress.setValue(0);
      setHeight(null);
      return;
    }
    if (phase === 'open') {
      progress.setValue(1);
      setHeight(null);
      return;
    }
    if (phase !== 'closing') return;
    setHeight(heightRef.current);
    const close = Animated.timing(progress, {
      toValue: 0,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    close.start(({ finished }) => {
      if (finished) setPhase('closed');
    });
    return () => close.stop();
  }, [phase, progress, motion]);

  // Opening grows to the measured height from wherever the value is — 0 after
  // closed, part-way after an interrupted close.
  useEffect(() => {
    if (phase !== 'opening' || height === null) return;
    const grow = Animated.timing(progress, {
      toValue: 1,
      duration: motion.duration.base,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: false,
    });
    grow.start(({ finished }) => {
      if (finished) setPhase('open');
    });
    return () => grow.stop();
  }, [phase, height, progress, motion]);

  if (phase === 'closed') return null;

  // The outer view has a height of its own (0 until measured, then the animated one): measure out of flow.
  const pinned = phase !== 'open' && (height !== null || phase === 'opening');
  const style =
    phase === 'open'
      ? undefined
      : height !== null
        ? [
            foldLayoutStyles.clip,
            {
              height: progress.interpolate({ inputRange: [0, 1], outputRange: [0, height] }),
              opacity: progress,
            },
          ]
        : phase === 'opening'
          ? [foldLayoutStyles.clip, foldLayoutStyles.unmeasured]
          : { opacity: progress }; // closing before any measurement: fade only

  return (
    <Animated.View style={style}>
      <View onLayout={handleLayout} style={[innerStyle, pinned && foldLayoutStyles.outOfFlow]}>
        {children}
      </View>
    </Animated.View>
  );
};

Fold.displayName = 'Fold';
